import { useState } from 'react';
import { toast } from 'react-toastify';
import BillingContactExternalForm from 'components/billingContacts/BillingContactExternalForm';
import {
  contactFormFrom,
  emptyContactForm,
  toContactPayload,
  validateContactForm,
} from 'components/billingContacts/billingContactForm';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import crmApi from 'server/crm/crmApi';
import { useInviteOnJobbiz } from './billingContactModal';

export const ADD_COMPANY_CONTACT_MODAL = 'add_company_contact';

// Keys of companyBillingContacts.errors, by field, as the billing form reads them.
const ERROR_TEXTS = {
  required: 'Ce champ est requis.',
  nameTooLong: 'Ce nom est trop long.',
  emailInvalid: 'Cette adresse e-mail n’est pas valide.',
  phoneInvalid: 'Ce numéro n’est pas valide.',
};
const API_ERRORS = {
  INVALID_CONTACT: 'Vérifiez le nom, l’e-mail et le téléphone du contact.',
  COMPANY_CONTACT_FORBIDDEN: 'Vous ne pouvez pas modifier les contacts de cette entreprise.',
  COMPANY_CONTACTS_FULL: 'Cette entreprise a déjà le nombre maximum de contacts.',
  COMPANY_CONTACT_NOT_FOUND: 'Ce contact n’existe plus.',
  COMPANY_NOT_FOUND: 'Cette entreprise n’existe plus.',
};

/**
 * « Ajouter un contact » of a company (JBWEB-1424): by default a contact
 * without an account, the billing contact's form without its boxes
 * (BillingExternalView), which « Inviter sur Jobbiz » turns into an invitation
 * (add_company_member, filled with what was typed). `modalData`: { idCompany,
 * contact? } — with `contact`, the contact is edited, or deleted.
 */
const ModalAddCompanyContact = () => {
  const { toggle } = useModal();
  const { modalData } = UseContext();
  const { call } = useCrmContext();
  const notify = useNotifyRecordChanged();
  const inviteOnJobbiz = useInviteOnJobbiz();
  // Read once: « Inviter sur Jobbiz » replaces « modalData » for the next modal.
  const [{ idCompany, contact }] = useState(() => ({ idCompany: modalData?.idCompany, contact: modalData?.contact ?? null }));
  const isEdit = Boolean(contact);
  const [form, setForm] = useState(() =>
    isEdit ? contactFormFrom(contact) : emptyContactForm(),
  );
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const changeField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const run = (request, message) => {
    setIsSending(true);
    request.then(
      () => {
        setIsSending(false);
        toast.success(message);
        notify();
        toggle();
      },
      (error) => {
        setIsSending(false);
        if (error?.name === 'CanceledError') return;
        toast.error(API_ERRORS[error?.response?.data?.errorCode] ?? 'L’enregistrement n’a pas abouti. Réessayez.');
      },
    );
  };

  const save = () => {
    const found = validateContactForm(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    run(
      call(crmApi.saveCompanyContact, {
        companyId: idCompany,
        contact: { ...(isEdit ? { id: contact.id } : {}), ...toContactPayload(form) },
      }),
      isEdit ? 'Contact enregistré' : 'Contact ajouté',
    );
  };

  const errorTexts = Object.fromEntries(
    Object.entries(errors)
      .filter(([, key]) => key)
      .map(([field, key]) => [field, ERROR_TEXTS[key] ?? key]),
  );
  const name = [contact?.firstname, contact?.lastname].filter(Boolean).join(' ') || contact?.email;

  if (isConfirmingDelete) {
    return (
      <>
        <Modalheader hide={toggle} hideShow={!isSending}>
          {`Supprimer ${name} ?`}
        </Modalheader>
        <ModalBody>
          <p className="text-sm text-gray-700">Le contact sera retiré de l’entreprise.</p>
        </ModalBody>
        <ModalFooter>
          <Button size="LG" color={isSending ? 'DISABLED' : 'SECONDARY'} disabled={isSending} onClick={() => setIsConfirmingDelete(false)}>
            Annuler
          </Button>
          <Button
            size="LG"
            color={isSending ? 'DISABLED' : 'PRIMARY'}
            disabled={isSending}
            onClick={() => run(call(crmApi.deleteCompanyContact, { companyId: idCompany, contactId: contact.id }), 'Contact supprimé')}
          >
            {isSending ? 'Suppression…' : 'Supprimer'}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <Modalheader
        hide={toggle}
        hideShow={!isSending}
        description="Un contact de l’entreprise, sans compte Jobbiz. Vous pouvez aussi l’inviter sur Jobbiz."
      >
        {isEdit ? 'Modifier le contact' : 'Ajouter un contact'}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <BillingContactExternalForm
            form={form}
            errors={errorTexts}
            onChange={changeField}
            isDisabled={isSending}
            withNotifications={false}
            withPosition
          />
          {isEdit && (
            <button
              type="button"
              disabled={isSending}
              onClick={() => setIsConfirmingDelete(true)}
              className="self-start text-sm text-red-600 underline underline-offset-4 disabled:text-gray-400"
            >
              Supprimer le contact
            </button>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          disabled={isSending}
          onClick={() => inviteOnJobbiz(idCompany, form)}
          className="mr-auto text-sm text-gray-900 underline decoration-dotted underline-offset-4 disabled:text-gray-400"
        >
          Inviter sur Jobbiz
        </button>
        <Button size="LG" color={isSending ? 'DISABLED' : 'SECONDARY'} disabled={isSending} onClick={toggle}>
          Annuler
        </Button>
        <Button size="LG" color={isSending ? 'DISABLED' : 'PRIMARY'} disabled={isSending} onClick={save}>
          {isSending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAddCompanyContact;
